import React, {useContext, useEffect, useState} from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import CustomLoader from "../../../../../util/component/CustomLoader";
import KeylessInfo from "./KeylessInfo";
import KeylessOperationArea from "./KeylessOperationArea";
import { useRecoilState, useRecoilValue } from "recoil";
import { keylessInfoState, keylessAreaState, keylessErrorState } from "../../../search-dms/state";
import { createKeylessInfoAndArea } from "../../../search-dms/dmsSearch.actions";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { SET_SNACKBAR } from "../../../common/utils";
import { useHasScope } from "../../../../../config/globalConfig";
import { NetworkScope } from "../../../types";
import { EditRounded } from '@mui/icons-material';
import { snackbarTypes } from "../../../../../util/state/globalVariables";

const Keyless = () => {
    const {t} = useTranslation(['network', 'translation']);
    const [isLoading, setIsLoading] = useState(false);
    const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
        e.stopPropagation();
    }

    const theme = useTheme();

    const [keylessInfo, setKeylessInfo] = useRecoilState(keylessInfoState);
    const [keylessArea, setKeylessArea] = useRecoilState(keylessAreaState);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const hasScope = useHasScope;

    const initialCreateNew = !keylessInfo?.id && hasScope(NetworkScope.MsMng)
    const [createNew, setCreateNew] = useState(false)
    const [infoAccExpanded, setInfoAccExpanded] = useState(createNew);
    const [areaAccExpanded, setAreaAccExpanded] = useState(createNew);

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [saveInfo, setSaveInfo] = useState(false);
    const [saveArea, setSaveArea] = useState(false);
    const [shouldSaveNew, setShouldSaveNew] = useState(false);
    const [payload, setPayload] = useState<any>(null);
    const [payloadInvalid, setPayloadInvalid] = useState<boolean|null>(null);
    const [fullPayload, setFullPayload] = useState({
        keylessInfoUpdateRequest: null as any,
        msKeylessArea: null as any,
    });
    const [isSaving, setIsSaving] = useState(false);

    function handleNewKeyless() {
        //console.log("Keyless: Collecting data for upsert ...");
        setIsLoading(true);
        setShouldSaveNew(true);
    }

    function resetNewKeylessFlags() {
        setSaveInfo(false);
        setSaveArea(false);
        setIsLoading(false);
        setShouldSaveNew(false);
        handleClose();
        setPayloadInvalid(false);
    }

    function submitFullPayload() {
        //console.log("Keyless: Collecting Keyless Area for upsert ...");
        //console.log(fullPayload);
        createKeylessInfoAndArea(fullPayload)
            .then((data: any) => {
                setKeylessInfo(data.msKeylessInfo);
                setKeylessArea(data.msKeylessArea);
                resetNewKeylessFlags();
                setCreateNew(false);
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            })
            .catch(() => {
                dispatchGlobalData(SET_SNACKBAR.ERROR);
                resetNewKeylessFlags();
            });
    }

    useEffect(() => {
        if (payloadInvalid == null) return

        if (payloadInvalid) {
            //console.log("Keyless: Data collection for upsert failed!");
            alert(t("dmsDetail.keyless.messages.requiredFieldsNotFilledIn"));
            resetNewKeylessFlags();
        }
    }, [payloadInvalid])

    useEffect(() => {
        if (isSaving) return;

        if (shouldSaveNew) {
            //console.log("Keyless: Collecting Keyless Info for upsert ...");
            setShouldSaveNew(false);
            setSaveInfo(true);
            return;
        }

        if (saveInfo && payload) {
            //console.log("Keyless: Got Keyless Info for upsert!");
            //console.log(payload);
            setSaveInfo(false);
            setPayload(null);
            setFullPayload({
                ...fullPayload,
                keylessInfoUpdateRequest: payload
            });
            setSaveArea(true);
            return;
        }

        if (saveArea && payload) {
            //console.log("Keyless: Got Keyless Area for upsert!");
            //console.log(payload);
            setSaveArea(false);
            setPayload(null);
            setFullPayload({
                ...fullPayload,
                msKeylessArea: payload
            });
            return;
        }

        if (fullPayload.keylessInfoUpdateRequest && fullPayload.msKeylessArea) {
            handleClose();
            //console.log("Keyless: Submitting upsert ...");
            setIsSaving(true);
            submitFullPayload();
        }
    }, [shouldSaveNew, payload, fullPayload])

    useEffect(() => {
        if (createNew) {
            setInfoAccExpanded(true)
            setAreaAccExpanded(true)
        }
    }, [createNew])

    const [isKeyLess, setIsKeyLess] = useState(keylessInfo?.keyLess)
    function onIsKeylessChanged(value: boolean) {
        setIsKeyLess(value)
        if (value) {
            setInfoAccExpanded(true)
            setAreaAccExpanded(true)
        }
    }

    const error = useRecoilValue(keylessErrorState);
    useEffect(() => {
        if (!error) return;

        const errorDetails = error?.response?.data?.errorDetails;
        if (!errorDetails) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
            return;
        }

        // console.log("keyless error", errorDetails);

        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(`dmsDetail.keyless.errors.${errorDetails.split(/\s/).at(1)}`),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }, [error]);

    return (
        <>
            {/* {initialCreateNew && !createNew && (
                <Grid container item direction="row" xs={12} columnSpacing={2}>
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            <IconButton color={"accent"} onClick={() => {setCreateNew(true)}}>
                                <EditRounded/>
                            </IconButton>
                        </Stack>
                    </Grid>
                </Grid>
            )} */}

            <Accordion onChange={(e, expanded) => { handleAccordionOnChange(e, expanded); setInfoAccExpanded(expanded); }} expanded={infoAccExpanded}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="h6">
                        {t("dmsDetail.keyless.info")}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <KeylessInfo isNew={createNew} shouldSave={saveInfo} setPayload={setPayload} onIsKeylessChanged={onIsKeylessChanged} setPayloadInvalid={setPayloadInvalid} />
                </AccordionDetails>
            </Accordion>

            <Accordion onChange={(e, expanded) => { handleAccordionOnChange(e, expanded); setAreaAccExpanded(expanded); }} expanded={isKeyLess && areaAccExpanded} disabled={!isKeyLess}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="h6">
                        {t("dmsDetail.keyless.operationArea")}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <KeylessOperationArea isNew={createNew} shouldSave={saveArea} setPayload={setPayload} isKeyLess={isKeyLess} setPayloadInvalid={setPayloadInvalid} />
                </AccordionDetails>
            </Accordion>

            {/*
            <Accordion onChange={(e, expanded) => { handleAccordionOnChange(e, expanded); setAreaAccExpanded(expanded); }} expanded={isKeyLess && areaAccExpanded} disabled={!isKeyLess}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="h6">
                        {t("dmsDetail.keyless.operationArea")}
                        - Old
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <KeylessOperationAreaOld isNew={createNew} shouldSave={saveArea} setPayload={setPayload} isKeyLess={isKeyLess} setPayloadInvalid={setPayloadInvalid} />
                </AccordionDetails>
            </Accordion>
            */}

            {createNew && (
                <Grid item xs={12} sx={{marginTop: theme.spacing(2)}}>
                    <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                        <Button variant="contained" onClick={handleOpen}>
                            {t("dmsDetail.keyless.save")}
                        </Button>
                    </Stack>
                </Grid>
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.keyless.infoTab.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("dmsDetail.keyless.infoTab.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}  variant={"outlined"} color={"accent"}>
                        {t("dmsDetail.keyless.infoTab.dialog.action.cancel")}
                    </Button>
                    <Button onClick={handleNewKeyless} autoFocus>
                        {t("dmsDetail.keyless.infoTab.dialog.action.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomLoader loading={isLoading} />
        </>
    )
}

export default Keyless;